/**
 * FileContentView — the opened-file surface. **Replaces `ArtifactCard` AND `BrandedArtifactCard`**
 * (CR §3.4, Track 3 §5.1).
 *
 * The two deleted cards were 193 and 440 lines that differed only in which panes they offered, yet
 * each carried its own copy of the artifact query, the rejection-dismiss effect, the loading and
 * not-generated states, the pending-commit branch and the submit footer. F1 unifies them: one
 * component, one lifecycle, and a view mode that varies per file instead of a whole second card.
 *
 * ── THE NINE REUSED COMPONENTS ─────────────────────────────────────────────────────────────────
 * `ArtifactStatusBadge`, `RejectionBanner`, `CommitRequestPanel` (→ `DiffViewer`, `RejectDialog`)
 * and `SubmitCommitButton` are reused unchanged — Track 3 confirmed none of them couple to the
 * chat model, so none needed touching.
 *
 * ── EDIT MODE IS A SINGLE-PANE LIVE-FORMATTED MARKDOWN EDITOR ──────────────────────────────────
 * F1 hosted `InlineEditor` here: a raw markdown `<textarea>` beside a separately rendered HTML
 * preview. That component is now deleted and replaced by `MarkdownLiveEditor`, which drops in on
 * the identical props/`onSaved` contract. One pane, not two: the user edits the markdown source
 * and the source itself is visually formatted as they type (CodeMirror 6 paints the characters
 * that are already there — no markdown→HTML step, no serialisation, no round-trip, one
 * representation throughout). This is explicitly NOT the rich-text/WYSIWYG track: the user still
 * authors markdown syntax. The read-only MD Preview pane below still renders HTML via
 * `lib/markdown`, which is correct — it is a viewer, not an editor, so it has no source to drift
 * from.
 *
 * ── DOCKED CHAT + AUTO-TAGGING (CR Decision 1) ─────────────────────────────────────────────────
 * Edit mode offers a docked chat surface that is a filtered view onto the single project session,
 * opening with THIS file pre-tagged. That is what preserves the per-file ergonomics of the deleted
 * `ChatEditor` without a second conversation — see `ProjectChatView` for why a second session is
 * architecturally impossible rather than merely undesirable.
 */

import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toastIfNotProjectMember } from "@/lib/project-permissions";
import { EmptyState } from "@/components/ui/empty-state";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { markdownToHtml } from "@/lib/markdown";
import {
  ARTIFACT_LABELS,
  useArtifact,
  useDownloadBrandedDoc,
  useRegenerateBrandedDoc,
  type ArtifactType,
} from "@/hooks/useArtifacts";
import { ArtifactStatusBadge } from "@/components/projects/artifacts/ArtifactStatusBadge";
import { CommitRequestPanel } from "@/components/projects/artifacts/CommitRequestPanel";
import { MarkdownLiveEditor } from "@/components/projects/artifacts/MarkdownLiveEditor";
import { RejectionBanner } from "@/components/projects/artifacts/RejectionBanner";
import { SubmitCommitButton } from "@/components/projects/artifacts/SubmitCommitButton";
import { VersionHistory } from "@/components/projects/artifacts/VersionHistory";
import {
  BrandedDocPane,
  DOWNLOAD_FILENAMES,
  brandedPipelineFor,
  isBrandedType,
} from "./BrandedDocPane";
import { ViewModeToggle, type FileViewMode } from "./ViewModeToggle";
import { ProjectChatView } from "./chat/ProjectChatView";

export interface FileContentViewProps {
  projectId: string;
  artifactType: ArtifactType;
  userRole: string;
  userId: string;
  /**
   * Member-only actions (lib/project-permissions.ts): branded REGENERATE (manual, and the
   * automatic re-render after an inline save) and the docked chat composer / accept. Defaults to
   * true for standalone renders; `Phase0Page` passes its membership-aware `canManage`.
   */
  canManage?: boolean;
}

/** Read-only rendered markdown of the current SAVED content. */
function MarkdownPane({ content }: { content: string }): JSX.Element {
  return (
    <div
      className="rounded-lg border border-outlines bg-white p-6 text-sm text-text-primary"
      aria-label="Rendered markdown preview"
      // Source is our own generated markdown, HTML-escaped before rendering (see lib/markdown.ts).
      dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }}
    />
  );
}

export function FileContentView({
  projectId,
  artifactType,
  userRole,
  userId,
  canManage = true,
}: FileContentViewProps): JSX.Element {
  const {
    data: artifact,
    isLoading,
    isError,
    refetch,
  } = useArtifact(projectId, artifactType);

  const branded = isBrandedType(artifactType);
  const label = ARTIFACT_LABELS[artifactType] ?? artifactType;

  const [viewMode, setViewMode] = useState<FileViewMode>(
    branded ? "branded" : "md",
  );
  const [chatDocked, setChatDocked] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Dismiss state for the rejection banner — resets when a new rejection arrives.
  const [rejectionDismissed, setRejectionDismissed] = useState(false);
  const resolvedAt = artifact?.last_rejection?.resolved_at;
  useEffect(() => {
    setRejectionDismissed(false);
  }, [resolvedAt]);

  const { mutateAsync: fetchDownloadUrl } = useDownloadBrandedDoc(projectId);

  /**
   * Branded regeneration, reached from a hook rather than from a ref the Branded pane hands
   * upward. This component is always mounted while a file is open, so the trigger exists whether
   * or not the user has ever opened the Branded tab — which is precisely what the old
   * `regenRef.current?.()` could not promise. See `useRegenerateBrandedDoc` for the full account.
   */
  const { mutateAsync: regenerateBranded, isPending: isRegeneratingFromSave } =
    useRegenerateBrandedDoc(projectId);

  // Submit for Commit is gated on EITHER regeneration source — this one (inline save) or the
  // Branded pane's own Regenerate button, reported up via `onRegenerateStateChange`.
  const [isPaneRegenerating, setIsPaneRegenerating] = useState(false);
  // Defence in depth: the pane's report is only meaningful while the pane is MOUNTED, i.e. while the
  // Branded view is showing. Gating on viewMode means a stale `true` can never outlive the pane,
  // even if a future refactor drops its unmount cleanup.
  const isRegenerating =
    isRegeneratingFromSave || (viewMode === "branded" && isPaneRegenerating);

  const refresh = useCallback((): void => {
    void refetch();
  }, [refetch]);

  /**
   * After a markdown save, a branded artifact must re-run its render pipeline or the Branded Doc
   * pane keeps showing the previous rendering.
   *
   * This is the SAME call the chat-accept path makes (`useResolveProjectChatResult`) and the same
   * one the pane's manual Regenerate button makes — one mechanism, three entry points. It
   * previously went through `regenRef`, which was null unless the Branded tab had been opened, so a
   * save from a user who had never visited that tab updated the markdown and silently left the
   * branded document stale.
   *
   * A non-branded artifact is a no-op inside the hook, so there is no `branded` check here.
   */
  const handleSaved = useCallback((): void => {
    refresh();
    // A non-member's re-render would be refused (403 NOT_PROJECT_MEMBER); don't fire it.
    if (!canManage) return;
    void regenerateBranded({ artifactType }).catch((err: unknown) => {
      // Non-blocking: the markdown save already succeeded and is what the user asked for. A failed
      // re-render is recoverable from the Branded pane's Regenerate button, which says so.
      if (toastIfNotProjectMember(err)) return;
      toast.error(
        `${label} saved, but its branded document could not be re-rendered`,
      );
    });
  }, [refresh, regenerateBranded, artifactType, label, canManage]);

  /** Fetch a fresh presigned URL and trigger a browser download. */
  const handleDownload = useCallback(async (): Promise<void> => {
    if (!branded) return;
    const pipeline = brandedPipelineFor(
      artifactType as "preliminary_srs" | "kickoff_deck",
    );
    setDownloading(true);
    try {
      const result = await fetchDownloadUrl(pipeline);
      const a = document.createElement("a");
      a.href = result.download_url;
      a.download = DOWNLOAD_FILENAMES[pipeline];
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`${label} downloaded`);
    } catch {
      toast.error(
        `${label} not ready yet — generation may still be in progress`,
      );
    } finally {
      setDownloading(false);
    }
  }, [branded, artifactType, fetchDownloadUrl, label]);

  // ── Artifact-level loading / not-generated states ──
  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-outlines p-6 text-sm text-text-secondary"
        role="status"
        aria-label={`Loading ${label}`}
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading {label}…
      </div>
    );
  }

  // null = 404 ARTIFACT_NOT_FOUND (not yet generated) → design-system empty state.
  if (!artifact && !isError) {
    return (
      <EmptyState
        className="rounded-lg border border-outlines"
        icon={<FileText className="h-5 w-5" aria-hidden="true" />}
        title={`${label} has not been generated yet`}
        description="Run Phase 0 generation to create this artifact."
      />
    );
  }

  // isError = any other failure, including a PROJECT_NOT_FOUND 404 (no access to this project).
  if (!artifact) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-outlines p-6">
        <p className="text-sm text-text-secondary">
          {`Could not load ${label}. The server returned an unexpected error.`}
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const showRejectionBanner =
    artifact.status === "rejected" &&
    !!artifact.last_rejection &&
    !rejectionDismissed;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-text-primary">{label}</h2>
          {branded && (
            <Badge className="bg-[#27164F] text-xs text-white">
              Cloudelligent Branded
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ArtifactStatusBadge status={artifact.status} />
          {branded && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleDownload()}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Download className="mr-1 h-4 w-4" aria-hidden="true" />
              )}
              Download
            </Button>
          )}
        </div>
      </div>

      {showRejectionBanner && (
        <RejectionBanner
          lastRejection={artifact.last_rejection}
          onDismiss={() => setRejectionDismissed(true)}
        />
      )}

      {/* Body: pending-approval review view REPLACES the panes — there is nothing to edit while a
          commit is awaiting a decision, and offering an editor would imply otherwise. */}
      {artifact.status === "pending_commit" ? (
        <CommitRequestPanel
          artifact={artifact}
          projectId={projectId}
          artifactType={artifactType}
          userRole={userRole}
          onApproved={refresh}
          onRejected={refresh}
          onWithdrawn={refresh}
        />
      ) : (
        <div className="relative flex flex-col gap-3">
          <ViewModeToggle
            mode={viewMode}
            onChange={setViewMode}
            showBranded={branded}
          />

          {viewMode === "md" && <MarkdownPane content={artifact.content} />}

          {viewMode === "branded" && branded && (
            <BrandedDocPane
              projectId={projectId}
              artifactType={artifactType as "preliminary_srs" | "kickoff_deck"}
              active
              onRegenerateStateChange={setIsPaneRegenerating}
              canRegenerate={canManage}
            />
          )}

          {viewMode === "edit" && (
            <div className="flex flex-col gap-3">
              <MarkdownLiveEditor
                artifact={artifact}
                projectId={projectId}
                artifactType={artifactType}
                onSaved={handleSaved}
              />

              {/* Docked chat (CR Decision 1). Collapsed by default so Edit mode opens as an editor,
                  not as a split screen; opening it auto-tags this file. */}
              {chatDocked ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium text-text-primary">
                      Chat about this file
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setChatDocked(false)}
                    >
                      Hide chat
                    </Button>
                  </div>
                  <ProjectChatView
                    projectId={projectId}
                    variant="docked"
                    initialTags={[artifactType]}
                    canWrite={canManage}
                  />
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => setChatDocked(true)}
                >
                  {`Ask the project chat about ${label}`}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Version history (Item 4 / §5.5, AD-10) — below the pane, above the submit footer.
          Renders nothing when the artifact has no approved commits, so it is invisible until the
          first commit lands. This is what makes the committed banner's "stays in the project's
          history" promise checkable rather than a claim.

          `enabled` skips the request entirely for an artifact that cannot have history yet. A
          freshly `generated` artifact has never been through an approval cycle by definition. */}
      <VersionHistory
        projectId={projectId}
        artifactType={artifactType}
        enabled={artifact.status !== "generated"}
      />

      {/* Submit-for-commit action (visible to PM/SA/admin; gated internally).
          Disabled while a branded regeneration is in flight — committing mid-regeneration would
          submit stale content. Re-enabled automatically when the regeneration settles. */}
      <div className="flex justify-end border-t border-outlines pt-3">
        <SubmitCommitButton
          artifact={artifact}
          projectId={projectId}
          artifactType={artifactType}
          userRole={userRole}
          onSubmitted={refresh}
          disabled={isRegenerating}
          disabledLabel="Regenerating — please wait"
        />
      </div>

      {/* userId is part of the view contract (submitter identity checks live in child panels via
          the auth context). Referenced here so the prop is retained in the public API. */}
      <span className="sr-only" data-user-id={userId} />
    </div>
  );
}

export default FileContentView;
